import React from 'react'
import delve from 'dlv'
import { connect } from 'react-redux'
import { getFormSyncErrors } from 'redux-form'
import { Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'

const useStyles = makeStyles((theme) => ({
  formLabelRoot: {
    marginBottom: theme.spacing(1.5)
  },
  formGroupRoot: {
    marginLeft: theme.spacing(0.5)
  },
  checkboxRoot: {
    marginBottom: theme.spacing(0.5)
  },
  checkboxLabel: {
    color: theme.palette.text.primary,
    marginLeft: theme.spacing(1)
  }
}))

const MuiMultiselectField = ({
  name: multiselectName,
  label,
  data,
  meta,
  question: { label: questionLabel = label, data: questionData = data } = {},
  ...props
}) => {
  const classes = useStyles()
  return (
    <FormControl className='multi-select' component='fieldset' error={meta.touched && !!meta.error}>
      <FormLabel component='legend' focused={false} className={classes.formLabelRoot}>
        <Typography style={{ fontWeight: 500 }}>
          {questionLabel}
        </Typography>
      </FormLabel>
      <FormGroup className={classes.formGroupRoot}>
        {questionData.map(({ name, value }) => {
          const fieldName = `${multiselectName}.${value}`
          const namedProps = props[multiselectName]
          const reduxProps = namedProps ? namedProps[value] : props[value]
          const inputProps = reduxProps || props[value]
          return (
            <FormControlLabel
              classes={{
                label: classes.checkboxLabel,
                root: classes.checkboxRoot
              }}
              control={ (
                <Checkbox
                  {...inputProps.input}
                  color='primary'
                  checked={!!inputProps.input.value}
                  name={fieldName}
                  id={fieldName}
                />
              )}
              label={name}
              key={value}
            />
          )
        })}
      </FormGroup>
      {meta.touched && !!meta.error && <FormHelperText error>{meta.error}</FormHelperText>}
    </FormControl>
  )
}

export default connect((state, ownProps) => {
  // A multiselect field is provided with a
  // root name for the field itself, along with
  // the names of the options that can be selected
  const { names, name } = ownProps

  // We need to get the first provided field name
  // (which is a dot-notation path)
  const [firstFieldPath] = names

  // We use the path to drill into the redux-form
  // properties to dynamically determine the current
  // form name
  const formName = delve(ownProps, `${firstFieldPath}.meta.form`)

  // Once we know the form name we can select all
  // the synchronous errors the form currently has
  const errors = getFormSyncErrors(formName)(state)

  // And because we also have a root name for the
  // field itself, we can pull of any error that might
  // have been returned for the multiselect
  const error = errors[name]

  // See if any of it's options have been touched
  const someTouched = names.some(fieldPath =>
    delve(ownProps, `${fieldPath}.meta.touched`)
  )

  // See if all of it's options have been touched
  const allTouched = names.every(fieldPath =>
    delve(ownProps, `${fieldPath}.meta.touched`)
  )

  // We'll consider the multiselect active if any of
  // it's options are active
  const active = names.some(fieldPath =>
    delve(ownProps, `${fieldPath}.meta.active`)
  )

  // Consider the multiselect touched if all of
  // it's options have been touched, or if some
  // of the options are touched and none of them
  // are active
  const touched = allTouched || (!active && someTouched)

  // Return a common meta structure like the meta
  // returned on a redux-form Field
  return {
    meta: { touched, error }
  }
})(MuiMultiselectField)
